import { axios } from '@/lib/axios';
import { FeatureFlagsResponse, GetEducationFeaturesResponse } from '@/features/FeatureFlags/types';
import { useQuery } from 'react-query';
import { GetListParams } from '@/types';
import { parseEducationFeatures } from './parseEducationFeatures';

export const EDUCATION_FEATURES_SITE_SETTING_NAME = 'educationFeatures';

export { parseEducationFeatures };

export const getEducationFeatures = (): Promise<GetEducationFeaturesResponse> => {
    return axios.get(`/api/admin/site_settings/${EDUCATION_FEATURES_SITE_SETTING_NAME}`);
};

export const useEducationFeatures = (institutionId?: number | null) => {
    return useQuery({
        queryKey: ['educationFeatures', institutionId],
        queryFn: getEducationFeatures,
        select: (response: GetEducationFeaturesResponse) => {
            const parsed = parseEducationFeatures(response, institutionId);
            return {
                ...parsed,
                isAdminQuizV2: parsed.enableQuizV2Api,
                response,
            };
        },
    });
};

type FilterCollection = {
    bool_operator?: string;
    condition?: {
        property: string;
        operator: string;
        value: string | number | boolean;
    };
};

type GetListBodyStructure = {
    navigation: {
        page: number;
        itemsOnPage: number;
    };
    totalCount: boolean;
    filter: {
        collection: FilterCollection[] | null;
    };
    sort: string[][];
};

export const buildFeatureFlagsListParams = (
    activePage: number,
    activeSearchKeyword: string | number,
    activeSortDirection: string,
    isNumber: boolean | undefined,
    activeSortProperty: string
) => {
    const isKeywordNumber = isNumber || Number.isInteger(activeSearchKeyword);
    return {
        navigation: {
            page: activePage,
            itemsOnPage: 20,
        },
        totalCount: true,
        filter: {
            collection: [
                ...(activeSearchKeyword
                    ? [
                          {
                              bool_operator: 'or',
                              condition: {
                                  property: isKeywordNumber ? 'id' : 'name',
                                  operator: isKeywordNumber ? 'equals' : 'contains',
                                  value: isKeywordNumber ? Number(activeSearchKeyword) : activeSearchKeyword || '',
                              },
                          },
                      ]
                    : []),
            ],
        },
        sort: [[activeSortProperty, activeSortDirection]],
    };
};

export const getFeatureFlagsList = (data: GetListBodyStructure): Promise<FeatureFlagsResponse> => {
    return axios.post(`/api/admin/site_settings/list`, data);
};

export const useFeatureFlags = ({ activePage, activeSearchKeyword, activeSortDirection, isNumber, activeSortProperty }: GetListParams) => {
    return useQuery<FeatureFlagsResponse, Error>({
        queryKey: ['feature-flag-list', activePage, activeSearchKeyword, activeSortDirection, isNumber, activeSortProperty],
        queryFn: () => {
            const params = buildFeatureFlagsListParams(activePage, activeSearchKeyword, activeSortDirection, isNumber, activeSortProperty);
            return getFeatureFlagsList(params);
        },
        keepPreviousData: true,
    });
};
